import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider } from './context/AuthContext';

// Layouts
import UserLayout from './components/common/UserLayout';
import OrganizerLayout from './components/common/OrganizerLayout';
import AgentLayout from './components/common/AgentLayout';
import AdminLayout from './components/AdminLayout';

// User Pages
import Home from './pages/user/Home';
import EventList from './pages/user/EventList';
import EventDetail from './pages/user/EventDetail';
import Login from './pages/user/Login';
import Register from './pages/user/Register';
import MyTickets from './pages/user/MyTickets';
import MyOrders from './pages/user/MyOrders';
import Profile from './pages/user/Profile';
import VenueList from './pages/user/VenueList';
import UserPaymentCallback from './pages/user/PaymentCallback';

// Organizer Pages
import OrganizerLogin from './pages/organizer/OrganizerLogin';
import OrganizerDashboard from './pages/organizer/Dashboard';
import OrganizerEvents from './pages/organizer/Events';
import CreateEvent from './pages/organizer/CreateEventWizard';
import PaymentCallback from './pages/organizer/PaymentCallback';
import ManageAgents from './pages/organizer/ManageAgents';
import OrganizerProfile from './pages/organizer/Profile';
import OrganizerFinancialReports from './pages/organizer/OrganizerFinancialReports';
import OrganizerSettings from './pages/organizer/Settings';
import OrganizerOrders from './pages/organizer/Orders';
import OrganizerTickets from './pages/organizer/Tickets';
import SeatMapEditor from './pages/organizer/SeatMapEditor';

// Agent Pages
import AgentLogin from './pages/agent/Login';
import AgentDashboard from './pages/agent/Dashboard';
import ScanTicket from './pages/agent/ScanTicket';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/Dashboard';
import ManageOrganizers from './pages/admin/ManageOrganizers';
import ManageEvents from './pages/admin/ManageEvents';
import ManageUsers from './pages/admin/ManageUsers';
import ManageCategories from './pages/admin/ManageCategories';
import ManageOrders from './pages/admin/ManageOrders';
import AdminProfile from './pages/admin/Profile';
import AdminFinancialReports from './pages/admin/AdminFinancialReports';
import ManagePayouts from './pages/admin/ManagePayouts';
import AdminSettings from './pages/admin/Settings';
import ManageSeatMapTemplates from './pages/admin/ManageSeatMapTemplates';
import MobileSeatMapPage from './pages/mobile/MobileSeatMapPage';

// Protected Route Component
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicRoute from "./components/common/PublicRoute";

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public User Routes */}
            <Route path="/" element={<UserLayout />}>
              <Route index element={<EventList />} />
              <Route path="why-Ticketaty" element={<Home />} />
              <Route path="events" element={<EventList />} />
              <Route path="events/:id" element={<EventDetail />} />
              <Route path="venues" element={<VenueList />} />
              <Route path="payment-callback" element={<UserPaymentCallback />} />
                <Route
                    path="login"
                    element={
                        <PublicRoute>
                            <Login />
                        </PublicRoute>
                    }
                />
              <Route path="register" element={<Register />} />
              
              {/* Protected User Routes */}
              <Route path="my-tickets" element={
                <ProtectedRoute role="user">
                  <MyTickets />
                </ProtectedRoute>
              } />
              <Route path="my-orders" element={
                <ProtectedRoute role="user">
                  <MyOrders />
                </ProtectedRoute>
              } />
              <Route path="profile" element={
                <ProtectedRoute role="user">
                  <Profile />
                </ProtectedRoute>
              } />
            </Route>

            {/* Organizer Routes */}
              <Route
                  path="/organizer/login"
                  element={
                      <PublicRoute>
                          <OrganizerLogin />
                      </PublicRoute>
                  }
              />
            <Route path="/organizer" element={
              <ProtectedRoute role="organizer">
                <OrganizerLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/organizer/dashboard" replace />} />
              <Route path="dashboard" element={<OrganizerDashboard />} />
              <Route path="events" element={<OrganizerEvents />} />
              <Route path="events/create" element={<CreateEvent />} />
              <Route path="events/edit/:id" element={<CreateEvent />} />
              <Route path="events/:eventId/seat-map" element={<SeatMapEditor />} />
              <Route path="payment-callback" element={<PaymentCallback />} />
              <Route path="orders" element={<OrganizerOrders />} />
              <Route path="tickets" element={<OrganizerTickets />} />
              <Route path="agents" element={<ManageAgents />} />
              <Route path="financial" element={<OrganizerFinancialReports />} />
              <Route path="settings" element={<OrganizerSettings />} />
              <Route path="profile" element={<OrganizerProfile />} />
            </Route>

            {/* Agent Routes */}
              <Route
                  path="/agent/login"
                  element={
                      <PublicRoute>
                          <AgentLogin />
                      </PublicRoute>
                  }
              />
            <Route path="/agent" element={
              <ProtectedRoute role="agent">
                <AgentLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="dashboard" element={<AgentDashboard />} />
              <Route path="scan" element={<ScanTicket />} />
            </Route>

            {/* Admin Routes */}
              <Route
                  path="/admin/login"
                  element={
                      <PublicRoute>
                          <AdminLogin />
                      </PublicRoute>
                  }
              />
            <Route path="/admin" element={
              <ProtectedRoute role="admin">
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="organizers" element={<ManageOrganizers />} />
              <Route path="events" element={<ManageEvents />} />
              <Route path="users" element={<ManageUsers />} />
              <Route path="categories" element={<ManageCategories />} />
              <Route path="orders" element={<ManageOrders />} />
              <Route path="financial" element={<AdminFinancialReports />} />
              <Route path="payouts" element={<ManagePayouts />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="seat-map-templates" element={<ManageSeatMapTemplates />} />
              <Route path="seat-map-templates/:templateId/edit" element={<SeatMapEditor />} />
            </Route>

            {/* Mobile WebView — no layout, no auth guard; token passed via ?token= */}
            <Route path="/m/seat-map/:eventId" element={<MobileSeatMapPage />} />

            {/* 404 */}
            <Route path="*" element={<div className="text-center mt-20 text-2xl">404 - Page Not Found</div>} />
          </Routes>
          
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

